import { useCallback, useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import styles from './UpdateBanner.module.css';

/** An installed app can stay open for days: look for a new version regularly. */
const CHECK_EVERY_MS = 30 * 60 * 1000;

/**
 * Offers a new version of the site as soon as it is deployed. An app left in the
 * background takes it at once, so nobody is interrupted while using it.
 */
export function UpdateBanner() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => {
        if (navigator.onLine) void registration.update();
      };
      setInterval(check, CHECK_EVERY_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
    },
  });

  // The worker reloads the pages it controls; a page it does not control yet (first
  // visit) is reloaded by hand once the new version had time to take over.
  const update = useCallback(() => {
    void updateServiceWorker(true);
    setTimeout(() => window.location.reload(), 3000);
  }, [updateServiceWorker]);

  useEffect(() => {
    if (!needRefresh) return;
    const applyWhenHidden = () => {
      if (document.visibilityState === 'hidden') update();
    };
    applyWhenHidden();
    document.addEventListener('visibilitychange', applyWhenHidden);
    return () => document.removeEventListener('visibilitychange', applyWhenHidden);
  }, [needRefresh, update]);

  if (!needRefresh) return null;
  return (
    <div className={styles.banner} role="status">
      <RefreshCw size={18} aria-hidden="true" />
      <span>Une nouvelle version de PokéFolio est disponible.</span>
      <Button variant="primary" onClick={update}>
        Mettre à jour
      </Button>
    </div>
  );
}
